import {
  FundsInfo,
  FundsText,
  FundsList,
  KeyFigures,
  FundsDetail,
  FundContent,
  FundsPrices,
  Resolutions,
  KeyDataPrices,
  FundsDocuments,
  ManagementReport,
  FundsPerformance,
  FundsInfoParameter,
  KeyFiguresParameter,
  RequiredPublications,
  FundsDetailParameter,
  FundsContentParameter,
  KeyDataPricesParameter,
  ManagementReportParameter,
  FundsPerformanceParameter,
  PriipsData,
  HTTPStatusCode,
  TrancheInfos,
  TrancheInfosParameter,
  InfobreakFilter,
  FundsKeyFigureTableItem,
  FundsPerformanceKeyFigureData,
  FundsPerformanceTableItem,
  InfobreakFilterResponse,
  FundsListV3,
} from './index.types';
import { DomService } from '..';
import { FundsMatrixDownloadData } from './types/ib-fundsmatrix-list.types';
import { createLinkAndDownloadDoc } from './fund-api.utils';
import { DocumentDownloads } from './types/document-downloads.types';
import { ChartgeneratorPerformanceSeries, ChartgeneratorPerformanceTable, ChartgeneratorTexts } from './types/ib-chartgenerator.types';

const fundApiUrl = `${DomService.getApiConfig()}/api/cms-api/v2/`;
const fundApiUrlV3 = `${DomService.getApiConfig()}/api/cms-api/v3/`;
const blackoutUrl = `${DomService.getApiConfig()}/api/blackout`;

const country = DomService.getGlobalConfig()?.country === 'raiffeisen' ? 'AT' : DomService.getGlobalConfig()?.country?.toUpperCase() || 'AT';
const locale = DomService.getGlobalConfig()?.locale === 'rsi' ? 'de' : DomService.getGlobalConfig()?.locale || 'de';

export const FundApiService = {
  async getBlackoutInformation() {
    return fetch(blackoutUrl).then((res) => {
      if (res.status === HTTPStatusCode.UNAUTHORIZED) {
        throw new Error(res.statusText);
      }

      return res.json();
    });
  },

  async getEndpoint(path: string, headers?: Headers, body?: any): Promise<unknown> {
    return fetch(fundApiUrl + path, {
      headers,
      body,
    }).then((res) => {
      if (res.status === HTTPStatusCode.UNAUTHORIZED) {
        throw new Error(res.statusText);
      }

      return res.json();
    });
  },

  async getEndpointV3(path: string, params?: {}, headers?: Headers, body?: any): Promise<unknown> {
    const queryParams = new URLSearchParams({
      ...params,
      country,
      language: locale,
    }).toString();

    return fetch(`${fundApiUrlV3}${path}?${queryParams}`, {
      headers,
      body,
    }).then((res) => {
      if (res.status === HTTPStatusCode.UNAUTHORIZED) {
        throw new Error(res.statusText);
      }

      return res.json();
    });
  },

  async getFundsList(channel: string): Promise<FundsList> {
    const data = (await FundApiService.getEndpoint(['fonds', country, channel, locale].join('/'))) as Promise<FundsList>;

    return data;
  },

  async getFundsListV3(distributionChannel: string): Promise<FundsListV3> {
    const data = (await FundApiService.getEndpointV3('funds', {
      distributionChannel: decodeURIComponent(distributionChannel),
    })) as Promise<FundsListV3>;

    return data;
  },

  async getManagementReport({ channel, fundId }: ManagementReportParameter): Promise<ManagementReport> {
    return FundApiService.getEndpoint(['managementreport', country, channel, locale, fundId].join('/')) as Promise<ManagementReport>;
  },

  async getPerformance({ channel, fundId, startDate, endDate }: unknown): Promise<unknown> {
    return FundApiService.getEndpoint(['performance', country, channel, locale, fundId, startDate, endDate].join('/')) as Promise<unknown>;
  },

  async getFondsinfo({ channel, fundId, keyDataTranche, performanceTranche }: FundsInfoParameter): Promise<FundsInfo> {
    return FundApiService.getEndpoint(
      ['fondsinfo', country, channel, locale, fundId, keyDataTranche, performanceTranche].join('/'),
    ) as Promise<FundsInfo>;
  },
  async getFondsDetail({ channel, fundId, primaertrancheId }: FundsDetailParameter): Promise<FundsDetail> {
    return FundApiService.getEndpoint(['fondsdetail', country, channel, locale, fundId, primaertrancheId].join('/')) as Promise<FundsDetail>;
  },

  async getFondskennzahlen({ channel, fundId, trancheId }: KeyFiguresParameter): Promise<KeyFigures> {
    return FundApiService.getEndpoint(['fondskennzahlen', country, channel, locale, fundId, trancheId].join('/')) as Promise<KeyFigures>;
  },

  async getFondstrancheinfos({ channel, fundId }: TrancheInfosParameter): Promise<TrancheInfos> {
    return FundApiService.getEndpoint(['trancheinfos', country, channel, locale, fundId].join('/')) as Promise<TrancheInfos>;
  },

  async getFondseckdatenundpreise({ channel, fundId, trancheId }: KeyDataPricesParameter): Promise<KeyDataPrices> {
    return FundApiService.getEndpoint(['fondseckdatenundpreise', country, channel, locale, fundId, trancheId].join('/')) as Promise<KeyDataPrices>;
  },

  async getDownload(hashcode: string): Promise<unknown> {
    return FundApiService.getEndpoint(['download', hashcode].join('/')) as Promise<unknown>;
  },

  async getFondsinhalt({ channel, fundId }: FundsContentParameter): Promise<FundContent> {
    return FundApiService.getEndpoint(['fondsinhalt', country, channel, locale, fundId].join('/')) as Promise<FundContent>;
  },

  async getFondsperformance({ channel, fundId, trancheId }: FundsPerformanceParameter): Promise<FundsPerformance> {
    return FundApiService.getEndpoint(['fondsperformance', country, channel, locale, fundId, trancheId].join('/')) as Promise<FundsPerformance>;
  },
  async getFondskurse(): Promise<FundsPrices> {
    return FundApiService.getEndpoint(['fondskurse', country, locale].join('/')) as Promise<FundsPrices>;
  },

  async getFondsdokumente(): Promise<FundsDocuments> {
    return FundApiService.getEndpoint(['fondsdokumente', country, locale].join('/')) as Promise<FundsDocuments>;
  },

  async getAufloesungen(): Promise<Resolutions> {
    return FundApiService.getEndpoint(['aufloesungen', country, locale].join('/')) as Promise<Resolutions>;
  },

  async getpFlichtveroeffentlichungen(): Promise<RequiredPublications> {
    return FundApiService.getEndpoint(['pflichtveroeffentlichungen', country, locale].join('/')) as Promise<RequiredPublications>;
  },

  async getDocuments({ channel, fundId, primaertrancheId }: unknown): Promise<DocumentDownloads> {
    return FundApiService.getEndpoint(
      ['dokumentedownloads', country, channel, locale, fundId, primaertrancheId].join('/'),
    ) as Promise<DocumentDownloads>;
  },

  async getFixtexte(kind: string): Promise<FundsText> {
    return FundApiService.getEndpoint(['texte', 'fixtexte', locale, `?texte=${kind}`].join('/')) as Promise<FundsText>;
  },

  async getFondstexte(fundId: string): Promise<FundsText> {
    return FundApiService.getEndpoint(['texte', 'fondstexte', country, locale, fundId].join('/')) as Promise<FundsText>;
  },

  async getRechtlicheTexte(): Promise<FundsText> {
    return FundApiService.getEndpoint(
      ['texte', 'rechtlicheTexte', `${locale}?land=${country}&emittent=Raiffeisen Kapitalanlage GmbH&zielgruppe=Publikumsfonds`].join('/'),
    ) as Promise<FundsText>;
  },

  async getPriipsData({ fundId, trancheId }: unknown): Promise<PriipsData> {
    return FundApiService.getEndpoint(['priips', country, locale, fundId, trancheId].join('/')) as Promise<PriipsData>;
  },

  async getInfobreakFundsList(token: string): Promise<FundsList> {
    const headers = new Headers([['Force-Origin', 'true']]);

    const data = (await FundApiService.getEndpoint(
      ['fonds', country, 'infoBreak', `${locale}?token=${token}`].join('/'),
      headers,
    )) as Promise<FundsList>;

    return data;
  },

  async getFundsDocumentMatrixData(token?: string): Promise<any> {
    const headers = new Headers([['Force-Origin', 'true']]);

    return FundApiService.getEndpoint(['infobreak', 'fonds', `?token=${token}`].join('/'), headers) as Promise<FundsText>;
  },

  async getFundsStructureData(fundId: string, token: string): Promise<FundContent> {
    const headers = new Headers([['Force-Origin', 'true']]);

    return FundApiService.getEndpoint(
      ['fondsinhalt', country, 'infoBreak', locale, `${fundId}?token=${token}`].join('/'),
      headers,
    ) as Promise<FundContent>;
  },

  async getFundsDocumentMatrixZipAllDocuments(fondsHash: string, token: string): Promise<FundsMatrixDownloadData> {
    return FundApiService.getEndpoint(
      ['infobreak', 'fonds', `zip?fondsHashes=${fondsHash}&token=${token}`].join('/'),
    ) as Promise<FundsMatrixDownloadData>;
  },

  async getFundsDocumentMatrixSingleDocument(documentHash: string, token: string): Promise<FundsMatrixDownloadData> {
    return FundApiService.getEndpoint(
      ['single', `download?documentHash=${documentHash}&token=${token}`].join('/'),
    ) as Promise<FundsMatrixDownloadData>;
  },

  async getFundsDocumentMatrixSelectedDocuments(documentsHashes: string[], token: string): Promise<FundsMatrixDownloadData> {
    const documentHashQueryParams = new URLSearchParams(documentsHashes.map((hash) => ['documentsHashes', hash]));
    return FundApiService.getEndpoint(
      ['infobreak', 'documents', `zip?${documentHashQueryParams.toString()}&token=${token}`].join('/'),
    ) as Promise<FundsMatrixDownloadData>;
  },

  async getFundsPerformance(date?: string, token?: string): Promise<FundsPerformanceKeyFigureData<FundsPerformanceTableItem>> {
    const headers = new Headers([['Force-Origin', 'true']]);
    const dateQuery = date ? `&date=${date}` : '';

    return FundApiService.getEndpoint(['infobreak', `performance?token=${token}${dateQuery}`].join('/'), headers) as Promise<
      FundsPerformanceKeyFigureData<FundsPerformanceTableItem>
    >;
  },

  async getFundsKeyFigure(date?: string, token?: string): Promise<FundsPerformanceKeyFigureData<FundsKeyFigureTableItem>> {
    const headers = new Headers([['Force-Origin', 'true']]);
    const dateQuery = date ? `&date=${date}` : '';
    return FundApiService.getEndpoint(['infobreak', `keyfigures?token=${token}${dateQuery}`].join('/'), headers) as Promise<
      FundsPerformanceKeyFigureData<FundsKeyFigureTableItem>
    >;
  },

  async getInfobreakFilters(): Promise<InfobreakFilterResponse[]> {
    const token = new URLSearchParams(window.location.search).get('token');
    const userId = new URLSearchParams(window.location.search).get('uid');

    return FundApiService.getEndpoint(['infobreak', 'ui', `filters?token=${token}&userId=${userId}`].join('/')) as Promise<InfobreakFilterResponse[]>;
  },

  async putInfobreakFilter(name: string, filter: InfobreakFilter): Promise<{ filterId: string }> {
    const token = new URLSearchParams(window.location.search).get('token');
    const userId = new URLSearchParams(window.location.search).get('uid');
    const body = { name, userId, filter: { ...filter } };

    return fetch(`${fundApiUrl}infobreak/ui/filters?token=${token}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => {
      if (res.status === HTTPStatusCode.UNAUTHORIZED) {
        throw new Error(res.statusText);
      }
      return res.json();
    });
  },

  async deleteInfobreakFilter(filterId: string): Promise<void> {
    const token = new URLSearchParams(window.location.search).get('token');

    return fetch(`${fundApiUrl}infobreak/ui/filters?token=${token}&filterId=${filterId}`, {
      method: 'DELETE',
    }).then((res) => {
      if (res.status === HTTPStatusCode.UNAUTHORIZED) {
        throw new Error(res.statusText);
      }
      return res.json();
    });
  },

  async getInfobreakFundlistDocument(type, tab, filter) {
    const token = new URLSearchParams(window.location.search).get('token');
    const path = ['infobreak', tab, type, `?token=${token}`].join('/');
    const url = `${fundApiUrl}${path}`;
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(filter),
      headers: {
        'content-type': 'application/json',
      },
    }).then(async (res) => {
      if (!res.ok) {
        return res.text();
      }

      createLinkAndDownloadDoc(res);
      return '';
    });
  },

  async getFundsPriceExcel(channel, isin, startDate, endDate, texts) {
    const path = ['fondspreiszeitreihe', country, channel, locale, isin, `?startDate=${startDate}&endDate=${endDate}&texts=${texts}`].join('/');
    const url = `${fundApiUrl}${path}`;
    return fetch(url).then(async (res) => {
      if (!res.ok) {
        return res.text();
      }

      createLinkAndDownloadDoc(res);
      return '';
    });
  },

  async getChartgeneratorTexts(trancheHashes: string[]): Promise<ChartgeneratorTexts> {
    const headers = new Headers([['Force-Origin', 'true']]);
    const token = new URLSearchParams(window.location.search).get('token');
    const trancheHashesParam = trancheHashes.join(',');

    return FundApiService.getEndpoint(
      ['infobreak', 'chartgenerator', `texts?trancheHashes=${trancheHashesParam}&token=${token}`].join('/'),
      headers,
    ) as Promise<ChartgeneratorTexts>;
  },

  async getChartgeneratorPerformanceTable(trancheHashes: string[]): Promise<ChartgeneratorPerformanceTable> {
    const headers = new Headers([['Force-Origin', 'true']]);
    const token = new URLSearchParams(window.location.search).get('token');
    const trancheHashesParam = trancheHashes.join(',');

    return FundApiService.getEndpoint(
      ['infobreak', 'chartgenerator', 'performance', `table?trancheHashes=${trancheHashesParam}&token=${token}`].join('/'),
      headers,
    ) as Promise<ChartgeneratorPerformanceTable>;
  },

  async getChartgeneratorPerformanceSeries(trancheHashes: string[]): Promise<ChartgeneratorPerformanceSeries> {
    const headers = new Headers([['Force-Origin', 'true']]);
    const token = new URLSearchParams(window.location.search).get('token');
    const trancheHashesParam = trancheHashes.join(',');

    return FundApiService.getEndpoint(
      ['infobreak', 'chartgenerator', 'performance', `series?trancheHashes=${trancheHashesParam}&token=${token}`].join('/'),
      headers,
    ) as Promise<ChartgeneratorPerformanceSeries>;
  },
};
